
import CardSelect from "../card-select";
import SizingCounters from "../counter/SizingCounters";
import InstructionSteps from "./instructionSteps";


export const REASON = {
    FITMENT_SIZE_ISSUES: "help_fitment_size_issues",
    QUALITY_ISSUES: "help_quality_issues",
    ORDER_DELAYED: "help_order_delayed",
    MISSING_OR_INCORRECT: "help_missing_or_incorrect",
    MAKE_CHANGES: "help_make_changes",
    WARRANTY: "help_warranty"
};

const OTHER_ISSUE = {
    WRONG_COVER: "other_wrong_cover",
    MISSING_AN_ITEM: "other_missing_an_item"
};

const WRONG_COVER_TYPE = {
    WRONG_COLOR: "wrong_wrong_color",
    WRONG_MODEL: "wrong_wrong_model"
};

const MISSING_ITEM_TYPE = {
    ANTENNA_PATCH: "missing_antenna_patch",
    CABLE_LOCK_SET: "missing_cable_lock_set",
    STRAPS: "missing_straps",
    RACHET_STRAPS: "missing_rachet_straps",
    STORAGE_BAG: "missing_storage_bag",
    SUPPORT_POLES: "missing_support_poles",
    OTHER: "missing_other"
};

const ORDER_DELAYED_ISSUE = {
    NO_TRACKING_NUMBER: "delayed_no_tracking_number",
    TRACKING_NOT_UPDATING: "delayed_tracking_not_updating",
    NOT_RECEIVED: "delayed_not_received"
};

const MAKE_CHANGES_TYPE = {
    CHANGE_YMM: "changes_change_ymm",
    CHANGE_COLOR: "changes_change_color",
    CHANGE_ADDRESS: "changes_change_address",
    ADD_TO_ORDER: "changes_add_to_order",
    CANCEL_ORDER: "changes_cancel_order"
};

const CANCEL_REASON = {
    ORDERED_BY_MISTAKE: "cancel_ordered_by_mistake",
    WRONG_CARD: "cancel_wrong_card",
    CHANGED_MIND: "cancel_changed_mind",
    SHIPPING_TOO_LONG: "cancel_shipping_too_long",
    ITEM_BACKORDER: "cancel_item_backorder",
    DISCOUNT_CODE: "cancel_discount_code",
    OTHER: "cancel_other"
};

const WARRANTY_ISSUE = {
    COVER_GOT_RIPPED: "warranty_cover_got_ripped",
    WATER_LEAKAGE: "warranty_water_leakage"
};

const QUALITY_ISSUE = {
    MATERIAL_DEFECT: "quality_material_defect",
    SEAM_STITCHING: "quality_seam_stitching",
    WATERPROOFING: "quality_waterproofing",
    STRAP_BUCKLE: "quality_strap_buckle",
    OTHER: "quality_other"
};

const FITMENT_ISSUE = {
    COVER_TOO_SMALL: "size_cover_too_small",
    COVER_TOO_LARGE: "size_cover_too_large",
    DOES_NOT_FIT_EXTENSIONS: "size_does_not_fit_extensions",
    INSTALLATION_UNCLEAR: "size_installation_unclear"
};

export const PRODUCT_TYPES = {
    CUSTOM: "Custom",
    CARS: "Cars",
    MOTORCYCLES: "Motorcycles",
    MOTORSPORTS: "Motorsports",
    BOATS: "Boats",
    JETSKIS: "Jetskis"

};

export const isPatioProduct = (item) => {
    const patioCategories = [
        "Patio",
        "Grill",
        "Industrial",
        "Custom Covers"
    ];

    return patioCategories.some(category => item?.customAttributes?._item_category?.includes(category));
};

const ICONS = {
    FIT_ISSUE: "FitIssue",
    QUALITY_ISSUE: "QualityIssue",
    WARRANTY: "Warranty",
    OTHERS: "Others",
    WRONG_COVER: "WrongCover",
    MISSING_ITEM: "MissingItem",
    SOME_ICON: "Someicon",
    WRONG_COLOR: "WrongColor",
    WRONG_MODEL: "WrongModel",
    ANTENNA_MISSING: "AntennaMissing",
    CABLE_MISSING: "CableMissing",
    STRAPS_MISSING: "StrapsMissing",
    STORAGE_BAG_MISSING: "StorageBagMissing",
    SUPPORT_POLE_MISSING: "SupportPoleMissing",
    RIPPED_COVER: "RippedCover",
    WATER_LEAKAGE: "WaterLeakage",
    DAMAGED_COVER: "DamagedCover",
    TOO_SMALL: "TooSmall",
    TOO_LARGE: "TooLarge",
    NO_TRACKING_NUMBER: "FileQuestionMark",
    TRACKING_NOT_UPDATING: "Clock",
    NOT_RECEIVED: "PackageX",
    CHANGE_YMM: "Car",
    CHANGE_COLOR: "Palette",
    CHANGE_ADDRESS: "MapPin",
    ADD_TO_ORDER: "PackagePlus",
    CANCEL_ORDER: "CircleX",
    ORDERED_BY_MISTAKE: "RotateCcw",
    WRONG_CARD: "CreditCard",
    CHANGED_MIND: "Brain",
    SHIPPING_TOO_LONG: "Hourglass",
    ITEM_BACKORDER: "PackageSearch",
    DISCOUNT_CODE: "Ticket",
    CANCEL_OTHER: "Ellipsis"
};

const fields = [
    // Main category selection
    {
        name: "reason",
        label: "What do you need help with?",
        component: CardSelect,
        helper: "",
        options: [
            {
                label: "My cover doesn’t fit",
                value: REASON.FITMENT_SIZE_ISSUES,
                icon: ICONS.FIT_ISSUE
            },
            {
                label: "There is a quality problem",
                value: REASON.QUALITY_ISSUES,
                icon: ICONS.QUALITY_ISSUE
            },
            {
                label: "My order is delayed",
                value: REASON.ORDER_DELAYED,
                icon: ICONS.OTHERS
            },
            {
                label: "Something is missing or incorrect",
                value: REASON.MISSING_OR_INCORRECT,
                icon: ICONS.MISSING_ITEM
            },
            {
                label: "I want to make changes in my order",
                value: REASON.MAKE_CHANGES,
                icon: ICONS.SOME_ICON
            },
            {
                label: "Warranty",
                value: REASON.WARRANTY,
                icon: ICONS.WARRANTY
            }
        ]
    },

    // Something is missing or incorrect subcategory
    {
        name: "otherIssue",
        helper: "We’re so sorry that something was missing or incorrect with your order. Our team is already working on getting this sorted out for you. We’ll contact you shortly with a resolution and make sure you get the correct items. Thank you for your understanding!",
        component: CardSelect,
        if: (data) => data.reason === REASON.MISSING_OR_INCORRECT,
        label: "What is the issue?",
        options: [
            {
                label: "Accessories missing (straps, storage bag, etc.)",
                patioLabel: "Accessories missing",
                value: OTHER_ISSUE.MISSING_AN_ITEM,
                icon: ICONS.MISSING_ITEM
            },
            {
                label: "Wrong item received",
                value: OTHER_ISSUE.WRONG_COVER,
                icon: ICONS.WRONG_COVER
            }
        ]
    },

    // Wrong item received options
    {
        name: "wrongCoverType",
        label: "Wrong item received",
        component: CardSelect,
        if: (data) => data.reason === REASON.MISSING_OR_INCORRECT && data.otherIssue === OTHER_ISSUE.WRONG_COVER,
        helper: "We’re sorry you received an incorrect cover. Please select your issue, add a brief description with a photo, and we’ll have the right cover on its way to you immediately.",
        options: [
            {
                label: "Wrong Color",
                value: WRONG_COVER_TYPE.WRONG_COLOR,
                icon: ICONS.WRONG_COLOR
            },
            {
                label: "Wrong Model",
                value: WRONG_COVER_TYPE.WRONG_MODEL,
                icon: ICONS.WRONG_MODEL
            }
        ]
    },

    // Missing Item options
    {
        name: "missingItemType",
        label: "Accessories missing (straps, storage bag, etc.)",
        component: CardSelect,
        if: (data) => data.reason === REASON.MISSING_OR_INCORRECT && data.otherIssue === OTHER_ISSUE.MISSING_AN_ITEM,
        helper: (productType) => {
            if (productType === PRODUCT_TYPES.CUSTOM) {
                return "We’re sorry for the incomplete delivery. Please let us know what you’re missing and we’ll sort it out.";
            }
            return "We’re sorry for the incomplete delivery. Please select the missing item - we’ll check our fulfillment logs and get the missing part to you as quickly as we can.";
        },
        options: [
            {
                label: "Antenna Patch",
                value: MISSING_ITEM_TYPE.ANTENNA_PATCH,
                productTypes: [PRODUCT_TYPES.CARS, PRODUCT_TYPES.MOTORCYCLES],
                icon: ICONS.ANTENNA_MISSING
            },
            {
                label: "Cable & Lock Set",
                value: MISSING_ITEM_TYPE.CABLE_LOCK_SET,
                productTypes: [PRODUCT_TYPES.CARS],
                icon: ICONS.CABLE_MISSING
            },
            {
                label: "Straps",
                value: MISSING_ITEM_TYPE.STRAPS,
                productTypes: [PRODUCT_TYPES.CARS, PRODUCT_TYPES.JETSKIS, PRODUCT_TYPES.MOTORSPORTS, PRODUCT_TYPES.BOATS],
                icon: ICONS.STRAPS_MISSING
            },
            {
                label: "Rachet Straps",
                value: MISSING_ITEM_TYPE.RACHET_STRAPS,
                productTypes: [PRODUCT_TYPES.BOATS],
                icon: ICONS.STRAPS_MISSING
            },
            {
                label: "Storage Bag",
                value: MISSING_ITEM_TYPE.STORAGE_BAG,
                productTypes: [PRODUCT_TYPES.CARS, PRODUCT_TYPES.MOTORCYCLES, PRODUCT_TYPES.JETSKIS, PRODUCT_TYPES.MOTORSPORTS],
                icon: ICONS.STORAGE_BAG_MISSING
            },
            // {
            //     label: "Support Poles",
            //     value: MISSING_ITEM_TYPE.SUPPORT_POLES,
            //     productTypes: [],
            //     icon: ICONS.SUPPORT_POLE_MISSING
            // },
            {
                label: "Other",
                patioLabel: "Tie-Downs / Grommets",
                value: MISSING_ITEM_TYPE.OTHER,
                productTypes: Object.values(PRODUCT_TYPES),
                icon: ICONS.OTHERS
            }
        ]
    },

    // Warranty subcategory
    {
        name: "warrantyIssue",
        label: "What do you need help with?",
        component: CardSelect,
        if: (data) => data.reason === REASON.WARRANTY,
        helper: "We’re sorry to hear about the issue with your cover. Our team will review your case and determine the best solution to make sure you’re satisfied. We’ll be in touch with next steps as soon as possible. Thanks for reaching out!",
        options: [
            {
                label: "My cover got ripped",
                value: WARRANTY_ISSUE.COVER_GOT_RIPPED,
                icon: ICONS.RIPPED_COVER
            },
            {
                label: "My cover started leaking",
                value: WARRANTY_ISSUE.WATER_LEAKAGE,
                icon: ICONS.WATER_LEAKAGE
            }
        ]
    },

    // Quality Issues subcategory
    {
        name: "qualityIssue",
        label: "What best describes the issue?",
        component: CardSelect,
        if: (data) => data.reason === REASON.QUALITY_ISSUES,
        helper: "We’re sorry to hear about the issue with your product. We strive to provide the best quality, and we’ll make sure to resolve this as quickly as possible. Our team will review your submission and contact you with the next steps. Thank you for helping us improve our products!",
        options: [
            {
                label: "Material defect",
                value: QUALITY_ISSUE.MATERIAL_DEFECT,
                icon: ICONS.DAMAGED_COVER
            },
            {
                label: "Seam / stitching issue",
                value: QUALITY_ISSUE.SEAM_STITCHING,
                icon: ICONS.QUALITY_ISSUE
            },
            {
                label: "Waterproofing issue",
                value: QUALITY_ISSUE.WATERPROOFING,
                icon: ICONS.WATER_LEAKAGE
            },
            {
                label: "Strap / buckle issue",
                value: QUALITY_ISSUE.STRAP_BUCKLE,
                icon: ICONS.STRAPS_MISSING
            },
            {
                label: "Other quality concern",
                value: QUALITY_ISSUE.OTHER,
                icon: ICONS.OTHERS
            }
        ]
    },

    // My order is delayed subcategory
    {
        name: "orderDelayedIssue",
        label: "What is the issue?",
        component: CardSelect,
        if: (data) => data.reason === REASON.ORDER_DELAYED,
        helper: "We apologize for the delay and understand how frustrating this can be. Our team is already looking into it and will send you an update shortly with a solution. Thank you for your patience and understanding!",
        options: [
            {
                label: "I didn’t receive a tracking number",
                value: ORDER_DELAYED_ISSUE.NO_TRACKING_NUMBER,
                icon: ICONS.NO_TRACKING_NUMBER
            },
            {
                label: "Tracking is not updating",
                value: ORDER_DELAYED_ISSUE.TRACKING_NOT_UPDATING,
                icon: ICONS.TRACKING_NOT_UPDATING
            },
            {
                label: "I did not receive my order",
                value: ORDER_DELAYED_ISSUE.NOT_RECEIVED,
                icon: ICONS.NOT_RECEIVED
            }
        ]
    },

    // I want to make changes in my order subcategory
    {
        name: "makeChanges",
        label: "What change do you want?",
        component: CardSelect,
        if: (data) => data.reason === REASON.MAKE_CHANGES,
        helper: "We’re sorry to hear that you need to cancel your order. We understand that sometimes plans change. We’ll proceed with the cancellation and send you an update shortly. Thank you for letting us know, and we hope to serve you again soon!",
        options: [
            {
                label: "Change the YMM",
                patioLabel: "Change measurements",
                value: MAKE_CHANGES_TYPE.CHANGE_YMM,
                icon: ICONS.CHANGE_YMM
            },
            {
                label: "Change the color",
                value: MAKE_CHANGES_TYPE.CHANGE_COLOR,
                icon: ICONS.CHANGE_COLOR
            },
            {
                label: "Change the address",
                value: MAKE_CHANGES_TYPE.CHANGE_ADDRESS,
                icon: ICONS.CHANGE_ADDRESS
            },
            {
                label: "Add to the order",
                value: MAKE_CHANGES_TYPE.ADD_TO_ORDER,
                icon: ICONS.ADD_TO_ORDER
            },
            {
                label: "Cancel my order",
                value: MAKE_CHANGES_TYPE.CANCEL_ORDER,
                icon: ICONS.CANCEL_ORDER
            }
        ]
    },

    // Cancellation reason (only when cancelling an order)
    {
        name: "cancelReason",
        label: "Why are you canceling?",
        component: CardSelect,
        if: (data) => data.reason === REASON.MAKE_CHANGES && data.makeChanges === MAKE_CHANGES_TYPE.CANCEL_ORDER,
        helper: "",
        options: [
            {
                label: "Ordered by mistake / duplicate order",
                value: CANCEL_REASON.ORDERED_BY_MISTAKE,
                icon: ICONS.ORDERED_BY_MISTAKE
            },
            {
                label: "I used the wrong card",
                value: CANCEL_REASON.WRONG_CARD,
                icon: ICONS.WRONG_CARD
            },
            {
                label: "Changed my mind",
                value: CANCEL_REASON.CHANGED_MIND,
                icon: ICONS.CHANGED_MIND
            },
            {
                label: "Shipping time too long",
                value: CANCEL_REASON.SHIPPING_TOO_LONG,
                icon: ICONS.SHIPPING_TOO_LONG
            },
            {
                label: "Item on backorder",
                value: CANCEL_REASON.ITEM_BACKORDER,
                icon: ICONS.ITEM_BACKORDER
            },
            {
                label: "Discount code didn’t work",
                value: CANCEL_REASON.DISCOUNT_CODE,
                icon: ICONS.DISCOUNT_CODE
            },
            {
                label: "Other",
                value: CANCEL_REASON.OTHER,
                icon: ICONS.CANCEL_OTHER
            }
        ]
    },

    // Fitment/Size Issues subcategory
    {
        name: "fitmentIssue",
        label: "What best describes the fit issue?",
        component: CardSelect,
        if: (data, productType) => data.reason === REASON.FITMENT_SIZE_ISSUES && productType !== PRODUCT_TYPES.CUSTOM,
        helper: "We understand how frustrating this can be. Don’t worry,  we’ll make this right quickly. Most fit issues are resolved with the correct replacement, and we’ll cover the shipping cost. Our team will reach out shortly to arrange the correct replacement. Thank you for your patience!",
        options: [
            {
                label: "Too small",
                value: FITMENT_ISSUE.COVER_TOO_SMALL,
                icon: ICONS.TOO_SMALL
            },
            {
                label: "Too large",
                value: FITMENT_ISSUE.COVER_TOO_LARGE,
                icon: ICONS.TOO_LARGE
            },
            {
                label: "Does not fit with extensions (spoiler, motor, accessories)",
                value: FITMENT_ISSUE.DOES_NOT_FIT_EXTENSIONS,
                icon: ICONS.OTHERS
            },
            {
                label: "Installation instructions unclear",
                value: FITMENT_ISSUE.INSTALLATION_UNCLEAR,
                icon: ICONS.SOME_ICON
            }
        ]
    },
    {
        name: "customInstructions",
        label: "Custom Instructions",
        helper: "It will help us a lot to verify this together down below are few steps if we can work together so we fix the issue",
        component: InstructionSteps,
        if: (data, productType) => {
            return data.reason === REASON.FITMENT_SIZE_ISSUES && productType === PRODUCT_TYPES.CUSTOM;
        }
    },

    // Cover too small - Instructions with steps
    {
        name: "fitmentSizeInstructions",
        label: "Instructions with steps",
        component: SizingCounters,
        if: (data) => data.reason === REASON.FITMENT_SIZE_ISSUES && data.fitmentIssue === FITMENT_ISSUE.COVER_TOO_SMALL,
    },
    {
        name: "fitmentSizeInstructions",
        label: "Size Adjustment",
        component: SizingCounters,
        if: (data) => {
            return data.reason === REASON.FITMENT_SIZE_ISSUES &&
                data.fitmentIssue === FITMENT_ISSUE.COVER_TOO_LARGE;
        }
    },

    {
        name: "briefDescription",
        label: "Provide a brief statement",
        component: "briefDescription",
        placeholder: "Enter your statement",
        helper: (_productType, data, item) => {
            if (data?.makeChanges !== MAKE_CHANGES_TYPE.CHANGE_YMM) {
                return "";
            }

            const attributes = item?.customAttributes || {};

            if (!isPatioProduct(item)) {
                return "";
            }

            const measurementKeys = attributes._measurements
                ? attributes._measurements.split("|").filter(Boolean)
                : [];

            const measurementsList = measurementKeys
                .filter((key) => attributes[key] != null && attributes[key] !== "")
                .map((key) => `${key}: ${attributes[key]}"`)
                .join("\n");

            if (!measurementsList) {
                return "Which one do you want to change";
            }

            return `These are the measurements you provided: \n\n${measurementsList}\n\nWhich measurement do you want to change?`;
        },
        hideImage: [REASON.ORDER_DELAYED, REASON.MAKE_CHANGES, FITMENT_ISSUE.INSTALLATION_UNCLEAR],
        requiredImage: [OTHER_ISSUE.WRONG_COVER, OTHER_ISSUE.MISSING_AN_ITEM, REASON.WARRANTY, REASON.FITMENT_SIZE_ISSUES, REASON.QUALITY_ISSUES],
        optionalImage: [],
        requiredDescription: [OTHER_ISSUE.MISSING_AN_ITEM],
        if: (data, productType) => {
            return data?.qualityIssue ||
                data?.warrantyIssue ||
                data?.fitmentSizeInstructions ||
                data?.wrongCoverType ||
                data?.missingItemType ||
                data?.orderDelayedIssue ||
                (data?.makeChanges && data.makeChanges !== MAKE_CHANGES_TYPE.CANCEL_ORDER) ||
                data?.cancelReason ||
                data?.fitmentIssue === FITMENT_ISSUE.DOES_NOT_FIT_EXTENSIONS ||
                data?.fitmentIssue === FITMENT_ISSUE.INSTALLATION_UNCLEAR ||
                data?.customInstructions ||
                (data?.reason === REASON.FITMENT_SIZE_ISSUES && productType === PRODUCT_TYPES.CUSTOM)
        }
    }
];

export default fields;